import React, { useMemo, useState } from 'react';
import { Card, Form, Badge } from 'react-bootstrap';
import styled from 'styled-components';

// Render câu hỏi Cloze (qtype multianswer) của học phần Mô Phỏng:
// video tình huống + 4 nhóm radio (Tên tình huống / Dấu hiệu GIÁN TIẾP /
// Dấu hiệu TRỰC TIẾP / Phương án XỬ LÝ). Question đến từ
// parseQuestionHTML → type 'multianswer' (xem parseClozeQuestionHTML).
//
// Video render bằng thẻ <video> thuần thay vì giữ markup videojs của Moodle
// (script videojs của Moodle không chạy trong SPA — giữ markup gốc sẽ ra
// player chết). Nguồn video là CDN public nên không cần token.

const VideoBox = styled.div`
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;

  video {
    display: block;
    width: 100%;
    max-height: 420px;
  }
`;

const SectionLabel = styled.div`
  font-weight: 600;
  margin: 14px 0 6px;
`;

// Tô nền nhẹ cho từng phương án ở chế độ xem lại: xanh = đáp án đúng,
// đỏ = lựa chọn sai của học viên.
const OptionRow = styled.div`
  border-radius: 6px;
  padding: 2px 8px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${(p) => (p.$correct ? '#e6f4ea' : p.$wrong ? '#fce8e6' : 'transparent')};

  .form-check {
    margin-bottom: 0;
  }
`;

const ClozeQuestionItem = ({
  question,
  slot,
  sequenceCheck,
  onAnswerChange,
  disabled = false,
}) => {
  // Khởi tạo từ radio đã checked (HV quay lại trang đã trả lời)
  const initialSelected = useMemo(() => {
    const sel = {};
    for (const sub of question.subQuestions || []) {
      const checked = sub.options.find((o) => o.checked);
      if (checked) sel[sub.inputName] = checked.value;
    }
    return sel;
  }, [question]);

  const [selected, setSelected] = useState(initialSelected);

  // Tổng kết đúng/sai theo từng phần — chỉ có ở chế độ XEM LẠI (disabled) khi
  // HTML Moodle kèm dữ liệu chấm. Luật: phải đúng CẢ 4 phần mới đạt câu này.
  const verdict = useMemo(() => {
    const subs = question.subQuestions || [];
    const graded = subs.map((s) => s.chosenCorrect).filter((v) => v === true || v === false);
    if (!disabled || graded.length === 0) return null;
    const numCorrect = subs.filter((s) => s.chosenCorrect === true).length;
    const total = subs.length;
    return { numCorrect, total, allCorrect: numCorrect === total };
  }, [question, disabled]);

  const handleChange = (inputName, value) => {
    if (disabled) return;
    const next = { ...selected, [inputName]: value };
    setSelected(next);

    // Gửi đủ mọi sub đã chọn (không chỉ sub vừa đổi) + sequencecheck —
    // cùng format save của multichoice thường, saveAnswer ở TestDetailPage
    // sẽ tự vá sequencecheck khi lệch.
    const payload = [
      ...Object.entries(next).map(([name, v]) => ({ name, value: String(v) })),
      { name: `q${question.id}:${slot}_:sequencecheck`, value: sequenceCheck },
      { name: `q${question.id}:${slot}_:flagged`, value: '0' },
    ];
    onAnswerChange(slot, payload);
  };

  return (
    <Card className='mb-3'>
      <Card.Body>
        {verdict && (
          <div className='mb-2'>
            <Badge bg={verdict.allCorrect ? 'success' : 'danger'} style={{ fontSize: '0.85rem' }}>
              Đúng {verdict.numCorrect}/{verdict.total} phần —{' '}
              {verdict.allCorrect ? 'Đạt câu này' : 'Chưa đạt (cần đúng cả 4 phần)'}
            </Badge>
          </div>
        )}
        {question.videoUrl && (
          <VideoBox>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video controls playsInline preload='metadata' src={question.videoUrl} />
          </VideoBox>
        )}
        {(question.subQuestions || []).map((sub) => {
          const showState = disabled && sub.chosenCorrect !== null;
          return (
            <div key={sub.inputName || sub.index}>
              <SectionLabel>
                {sub.label}
                {showState && (
                  <span className={`ms-2 fw-bold ${sub.chosenCorrect ? 'text-success' : 'text-danger'}`}>
                    {sub.chosenCorrect ? '✓ Đúng' : '✗ Sai'}
                  </span>
                )}
              </SectionLabel>
              <Form>
                {sub.options.map((opt, i) => {
                  const id = `${sub.inputName}_opt${i}`;
                  // Xem lại: xanh cho đáp án đúng (kể cả HV không chọn), đỏ cho
                  // lựa chọn sai của HV.
                  const isTheCorrect = opt.isCorrect || (sub.correctLabel && opt.label === sub.correctLabel);
                  const isWrongChosen = opt.isWrongChoice || (opt.checked && sub.chosenCorrect === false);
                  const rowCorrect = showState && isTheCorrect;
                  const rowWrong = showState && isWrongChosen && !isTheCorrect;
                  return (
                    <OptionRow key={id} $correct={rowCorrect} $wrong={rowWrong}>
                      <Form.Check
                        type='radio'
                        id={id}
                        name={sub.inputName}
                        label={opt.label}
                        value={opt.value}
                        checked={selected[sub.inputName] === opt.value}
                        onChange={() => handleChange(sub.inputName, opt.value)}
                        disabled={disabled && selected[sub.inputName] !== opt.value}
                      />
                      {rowCorrect && <small className='text-success text-nowrap'>Đáp án đúng</small>}
                      {rowWrong && <small className='text-danger text-nowrap'>Bạn đã chọn</small>}
                    </OptionRow>
                  );
                })}
              </Form>
            </div>
          );
        })}
        {question?.feedback && (
          <>
            <div className='mt-2'>
              <strong>Giải thích:</strong>
            </div>
            <div className='mt-2' dangerouslySetInnerHTML={{ __html: question.feedback }} />
          </>
        )}
        {question?.rightAnswer && (
          <div className='mt-2'>
            <strong>Đáp án:</strong>
            <div className='mt-2' dangerouslySetInnerHTML={{ __html: question.rightAnswer }} />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ClozeQuestionItem;
